import React from 'react';
import { useSyncStore } from '../../store/syncStore';

const SyncStatusPanel = () => {
  const {
    syncStatus,
    isLoadingStatus,
    statusError,
    fetchSyncStatus,
    isDataStale,
  } = useSyncStore();

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp || timestamp === '0001-01-01T00:00:00Z') {
      return 'Никогда';
    }
    
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return 'Неизвестно';
    }
  };

  const shouldLoadData = !syncStatus && !isLoadingStatus;
  const isDataOld = syncStatus && isDataStale('status', 5);

  if (shouldLoadData) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center">
          <div className="text-gray-500 text-lg mb-4">Статус синхронизации не загружен</div>
          <button
            onClick={fetchSyncStatus}
            className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 mx-auto"
          >
            <span>🔄</span>
            Загрузить статус
          </button>
        </div>
      </div>
    );
  }

  if (isLoadingStatus) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-gray-600">Загрузка статуса синхронизации...</span>
        </div>
      </div>
    );
  }

  if (statusError) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-4">Ошибка загрузки</div>
          <p className="text-gray-600 mb-4">{statusError}</p>
          <button
            onClick={fetchSyncStatus}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  if (!syncStatus) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-800">
          📊 Статус синхронизации
        </h3>
        <div className="flex gap-2">
          {isDataOld && (
            <span className="text-yellow-500 text-sm">⚠️ Данные устарели</span>
          )}
          <button
            onClick={fetchSyncStatus}
            className="text-blue-500 hover:text-blue-600 text-sm font-medium"
          >
            Обновить
          </button>
        </div>
      </div>

      {/* Статус выполнения */}
      <div className="mb-6">
        <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
          syncStatus.is_running 
            ? 'bg-yellow-100 text-yellow-800' 
            : 'bg-green-100 text-green-800'
        }`}>
          <div className={`w-2 h-2 rounded-full mr-2 ${
            syncStatus.is_running ? 'bg-yellow-500 animate-pulse' : 'bg-green-500'
          }`}></div>
          {syncStatus.is_running ? 'Синхронизация выполняется...' : 'Синхронизация не выполняется'}
        </div>
        {syncStatus.is_running && (
          <div className="mt-2 text-sm text-yellow-700">
            Пожалуйста, дождитесь завершения текущей синхронизации
          </div>
        )}
      </div>

      {/* Информация о времени */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-3">Последняя синхронизация</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Начало:</span>
              <span className="font-medium">{formatTimestamp(syncStatus.start_time)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Завершение:</span>
              <span className="font-medium">{formatTimestamp(syncStatus.end_time)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Последняя:</span>
              <span className="font-medium">{formatTimestamp(syncStatus.last_sync)}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-3">Автоматическая синхронизация</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Следующая:</span>
              <span className="font-medium">{formatTimestamp(syncStatus.next_auto_sync)}</span>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              Выполняется каждые 30 минут
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SyncStatusPanel;
